import { useCallback, useEffect, useMemo, useState } from "react";
import {
  LookupKind,
  LookupOption,
  getLookupOptions,
  invalidateLookup,
  subscribeToLookup,
} from "./lookupsApi";
import type { SearchableSelectOption } from "../components/SearchableSelect";

export interface UseLookupOptionsResult {
  options: SearchableSelectOption<number>[];
  raw: LookupOption[];
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * Lista de opções de uma entidade, já no formato do <SearchableSelect>.
 * Serve-se da cache partilhada — abrir a mesma view dez vezes faz um pedido.
 */
export function useLookupOptions(
  kind: LookupKind,
  options?: { includeInactive?: boolean; enabled?: boolean },
): UseLookupOptionsResult {
  const includeInactive = options?.includeInactive ?? false;
  const enabled = options?.enabled ?? true;

  const [raw, setRaw] = useState<LookupOption[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);

    getLookupOptions(kind, { includeInactive })
      .then((result) => {
        if (!active) return;
        setRaw(result);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (!active) return;
        setRaw([]);
        setError(
          cause instanceof Error ? cause.message : "Falha a carregar as opções",
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [kind, includeInactive, enabled, nonce]);

  // Quem invalidar a lista — mesmo noutro ecrã — faz este seletor voltar a buscá-la.
  useEffect(
    () =>
      subscribeToLookup((changed) => {
        if (changed === kind) setNonce((previous) => previous + 1);
      }),
    [kind],
  );

  const mapped = useMemo(
    () =>
      raw.map((option) => ({
        value: option.id,
        label: option.label,
        description: option.description ?? undefined,
        keywords: option.keywords ?? undefined,
      })),
    [raw],
  );

  const reload = useCallback(() => {
    invalidateLookup(kind);
    setNonce((previous) => previous + 1);
  }, [kind]);

  return { options: mapped, raw, loading, error, reload };
}
